import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Paper,
    IconButton
} from '@mui/material';
import { AlertTriangle, X } from 'react-feather';

/**
 * A reusable confirmation dialog for delete operations
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to call when dialog is closed without confirming
 * @param {Function} props.onConfirm - Function to call when delete is confirmed
 * @param {string} props.title - Dialog title
 * @param {string} props.message - Primary confirmation message
 * @param {string} props.warningMessage - Warning message to display (optional)
 * @param {React.ReactNode} props.itemContent - Content to display describing the item being deleted
 * @param {boolean} props.loading - Whether the delete operation is in progress
 */
const ConfirmDeleteDialog = ({
                                 open,
                                 onClose,
                                 onConfirm,
                                 title = "Confirm Delete",
                                 message = "Are you sure you want to delete this item?",
                                 warningMessage = "This action cannot be undone.",
                                 itemContent = null,
                                 loading = false
                             }) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '10px',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{ py: 2, px: 3, bgcolor: '#F9FAFB' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ color: '#EF4444', fontWeight: 600 }}>
                        {title}
                    </Typography>
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                        size="small"
                    >
                        <X size={18} />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    {message}
                </Typography>

                {warningMessage && (
                    <Box
                        sx={{
                            display: 'flex',
                            backgroundColor: '#FEF2F2',
                            p: 2,
                            borderRadius: '8px',
                            mb: 3
                        }}
                    >
                        <AlertTriangle size={20} color="#EF4444" style={{ marginRight: 8, flexShrink: 0 }} />
                        <Typography variant="body2" color="#B91C1C">
                            {warningMessage}
                        </Typography>
                    </Box>
                )}

                {itemContent && (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            backgroundColor: '#F9FAFB',
                            borderRadius: '8px',
                            border: '1px solid rgba(0, 0, 0, 0.08)'
                        }}
                    >
                        {itemContent}
                    </Paper>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, px: 3, bgcolor: '#F9FAFB' }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{ borderRadius: '8px' }}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    color="error"
                    sx={{ borderRadius: '8px' }}
                    disabled={loading}
                >
                    {loading ? "Deleting..." : "Delete"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmDeleteDialog;